import {StorageHelper} from "../../helpers/storage.helper";
import {INITIAL_STOPWATCH, sanitize, type StopwatchState} from "./stopwatch.state";
import {subscribeKeys} from "./storage-subscription";

/**
 * Where a stopwatch tile keeps what belongs to ONE viewer (feature plan 0071): its running state. Browser storage,
 * per tile id — not on the server, so the stopwatch runs for everybody separately, and keeps running across a
 * reload. The look this viewer switched to lives in `gimmick-skin.store.ts`, shared by every gimmick (0072).
 *
 * Every access is guarded here: `StorageHelper` catches a broken JSON value but not a throwing
 * `getItem`/`setItem` (storage blocked, quota full). Then the stopwatch still runs, it only forgets.
 *
 * Known blur, accepted: `StorageHelper.buildKey` lower-cases the key and folds `-`/`_`/blanks, so two tile ids
 * differing only in that would share a state. Entries of deleted tiles stay behind (a few bytes each).
 */

function stateKey(tileId: string): string {
    return `gimmicks.stopwatch.${tileId}`;
}

export function loadState(tileId: string, now: number): StopwatchState {
    try {
        return sanitize(StorageHelper.get<unknown>(stateKey(tileId)), now);
    } catch (error) {
        console.warn('The stopwatch state could not be read', error);
        return INITIAL_STOPWATCH;
    }
}

export function saveState(tileId: string, state: StopwatchState): void {
    try {
        StorageHelper.set(stateKey(tileId), state);
    } catch (error) {
        console.warn('The stopwatch state could not be stored', error);
    }
}

/**
 * Calls `onChange` when ANOTHER tab of this browser changed this tile's running state. The viewer's look has its
 * own store and subscription (`gimmick-skin.store.ts`, feature plan 0072).
 */
export function subscribe(tileId: string, onChange: () => void): () => void {
    return subscribeKeys([stateKey(tileId)], onChange);
}
